import moment from 'moment-timezone'
import { TheGreatProviderStateT } from '@/types/TheGreatProvider.types'
import { initialIncomingDateTime, isValidTimeParam } from '@/utils/dates.utils'
import { initialIncomingQueries } from '@/app/_providers/TheGreatProvider/TheGreatContext'
import { DAYPASS_URL_PARAM, TIMED_TICKET_URL_PARAM } from '@/config/navigation.config'

/**
 * Initial state for TheGreatProvider
 */
export const initialGreatProviderState: TheGreatProviderStateT = { incomingQueries: initialIncomingQueries }

/**
 * TheGreatProvider reducer
 * simply merge the old state with the next state
 * supports functional updates
 * @param {TheGreatProviderStateT} oldState
 * @param {
 *   Partial<TheGreatProviderStateT>
 *   | ((state: TheGreatProviderStateT) => Partial<TheGreatProviderStateT>)
 * } action
 * @returns {TheGreatProviderStateT}
 */
export const theGreatProviderReducer = (
  oldState: TheGreatProviderStateT,
  action: Partial<TheGreatProviderStateT> | ((state: TheGreatProviderStateT) => Partial<TheGreatProviderStateT>),
): TheGreatProviderStateT => {
  const nextState = typeof action === 'function' ? action(oldState) : action
  return { ...oldState, ...nextState }
}

/**
 * Incoming date and time data from url params
 * Split time into hours and minutes
 * If date is valid and not in the past (relative to venue timezone), set date and time
 * @param {IncomingUrlData} incomingUrlData
 * @param {string} timezone
 * @returns {IncomingDateTime}
 */
export const transformIncomingDateAndTime = (
  incomingUrlData: IncomingUrlData,
  timezone: string,
): IncomingDateTime => {
  const nextIncomingDateTime: IncomingDateTime = {
    ...initialIncomingDateTime,
    time: { ...initialIncomingDateTime.time },
  }

  const { date, time } = incomingUrlData
  const validDate = !!date && moment(date, 'YYYY-MM-DD').isValid()

  // handle date
  if (validDate) {
    /**
     * Create a moment object with the date and timezone, setting time to 10:00
     * NOTE: I don't remember exactly why we are using 10:00, but it had something to do with
     * DST changes - if the date time was 0:00, the converted time would sometimes be the wrong day
     * Setting it to 10:00 ensured that the date was always the correct day
     */
    const dateToMoment = moment.tz(`${date} 10:00`, timezone)
    const today = moment.tz(moment(), timezone)

    // Ignore incoming date if it's in the past relative to the venue timezone
    if (dateToMoment.isBefore(today, 'day')) {
      return nextIncomingDateTime
    }

    nextIncomingDateTime.date = date

    // handle time — only parse when the value is a valid hh:mm (00:00–24:00),
    // otherwise an invalid string (e.g. "blahblah") would cause a destructuring crash
    if (isValidTimeParam(time)) {
      const [hours, minutes] = time!.split(':')
      nextIncomingDateTime.time = {
        hours: parseInt(hours, 10),
        minutes: parseInt(minutes, 10),
      }
      nextIncomingDateTime.withTime = true
    }
  }

  return nextIncomingDateTime
}

/**
 * Handle incoming url data. Get it ready to be stored in state
 * @param {IncomingUrlData} urlParamData
 * @param {string} timezone
 * @returns {Partial<IncomingQueries>}
 */
export const handleIncomingUrlData = (
  urlParamData: IncomingUrlData,
  timezone: string,
): Partial<IncomingQueries> => {
  const nextIncomingQueries: Partial<IncomingQueries> = {}
  const {
    promoCodes,
    search,
    category,
    payment_intent_client_secret: paymentIntentClientSecret,
    cart_id: cartId,
    iid,
    skip,
    admission_type: admissionType,
  } = urlParamData

  // handle date and time
  nextIncomingQueries.dateTime = transformIncomingDateAndTime(urlParamData, timezone)

  // handle incoming promo codes
  if (promoCodes.length > 0) {
    nextIncomingQueries.promoCodes = promoCodes
  }
  // handle incoming search
  if (search.length > 0) {
    nextIncomingQueries.search = search
  }
  // handle incoming event category
  if (category.length > 0) {
    nextIncomingQueries.filter = category
  }
  if (paymentIntentClientSecret.length > 0) {
    nextIncomingQueries.paymentIntentClientSecret = paymentIntentClientSecret
  }
  if (cartId.length > 0) {
    nextIncomingQueries.cartId = cartId
  }
  if (iid.length > 0) {
    nextIncomingQueries.iid = iid
  }
  if (skip.length > 0) {
    nextIncomingQueries.skip = skip === 'true'
  }

  if (admissionType === DAYPASS_URL_PARAM || admissionType === TIMED_TICKET_URL_PARAM) {
    // if there is an incoming time param, we override the admission type to timed ticketing
    nextIncomingQueries.admissionType = nextIncomingQueries.dateTime?.withTime
      ? TIMED_TICKET_URL_PARAM
      : admissionType
  }

  return nextIncomingQueries
}
